import { ArrowRight, Check, Minus } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { Reveal, SplitReveal } from "./Motion";

const Path = ({ label, steps }) => (
  <div className="mt-6 border-t border-white/10 pt-4">
    <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</p>
    <ol className="flex flex-wrap gap-x-2 gap-y-1 text-xs text-white/65">
      {steps.map((s, i) => <li key={s} className="flex items-center gap-2">{i > 0 && <span className="text-[#d4af37]">→</span>}{s}</li>)}
    </ol>
  </div>
);

const List = ({ title, items, icon: Icon, muted }) => (
  <div>
    <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{title}</p>
    <ul className="space-y-2">
      {items.map((b) => <li key={b} className={`flex items-start gap-3 text-sm ${muted ? "text-white/55" : "text-white/80"}`}><Icon className={`mt-0.5 h-4 w-4 shrink-0 ${muted ? "text-white/30" : "text-[#e8c979]"}`} />{b}</li>)}
    </ul>
  </div>
);

const Card = ({ id, data, o, featured, onCta, note, extra }) => (
  <div data-testid={`offer-card-${id}`} className={`relative flex h-full flex-col rounded-[28px] border p-6 md:p-9 ${featured ? "border-[#d4af37]/45 bg-gradient-to-b from-[#d4af37]/[0.12] to-transparent" : "border-white/10 bg-white/[0.03]"}`}>
    <h3 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{data.name}</h3>
    <p className="mt-4 font-display text-4xl font-light gold-text md:text-5xl" data-testid={`offer-price-${id}`}>{data.price}</p>
    {data.price !== o.development.price && <p className="mt-1 text-[11px] text-white/50">{o.vatNote}</p>}
    <p className="mt-5 text-sm leading-relaxed text-white/75 md:text-base">{data.tagline}</p>
    <div className="mt-6 grid gap-6">
      <List title={o.includes} items={data.includes} icon={Check} />
      {data.excludes && <List title={o.excludes} items={data.excludes} icon={Minus} muted />}
    </div>
    {extra}
    {data.path && <Path label={o.path} steps={data.path} />}
    <div className="mt-auto pt-8">
      <button data-testid={`offer-cta-${id}`} onClick={onCta} className={`${featured ? "btn-gold" : "btn-ghost"} group w-full justify-center`}>
        {data.cta} <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
      </button>
      {note && <p className="mt-4 text-xs leading-relaxed text-white/55">{note}</p>}
    </div>
  </div>
);

export const Offers = ({ onRequest, onProject }) => {
  const { t } = useLang();
  const o = t.offers;

  return (
    <section id="offerte" data-testid="offers-section" className="grain relative overflow-hidden bg-[#0a0a0b] py-20 text-[#fafaf7] md:py-32">
      <div className="gold-line absolute inset-x-0 top-0 h-px opacity-40" />
      <div className="wrap">
        <div className="mb-12 grid gap-6 md:mb-16 md:grid-cols-12">
          <p className="eyebrow md:col-span-3">{o.eyebrow}</p>
          <div className="md:col-span-9">
            <h2 className="font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"><SplitReveal key={o.title} text={o.title} /></h2>
            <Reveal delay={0.2}><p className="mt-6 max-w-3xl text-sm leading-relaxed text-white/70 md:text-base">{o.sub}</p></Reveal>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-3 lg:gap-6">
          <Reveal className="h-full"><Card id="diagnosis" data={o.diagnosis} o={o} onCta={() => onRequest("diagnosis")} note={o.diagnosis.credit} extra={<p className="mt-5 text-xs leading-relaxed text-white/60">{o.diagnosis.sheet}</p>} /></Reveal>
          <Reveal delay={0.1} className="h-full"><Card id="consulting" data={o.consulting} o={o} featured onCta={() => onRequest("consulting")} note={`${o.consulting.scope} ${o.consulting.open}`} /></Reveal>
          <Reveal delay={0.2} className="h-full"><Card id="development" data={o.development} o={o} onCta={onProject} /></Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 grid gap-4 rounded-[28px] border border-white/10 p-6 md:grid-cols-[auto_1fr] md:items-center md:gap-10 md:p-8" data-testid="offers-compare">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{o.compare.title}</p>
            <ul className="grid gap-3 sm:grid-cols-3">
              {o.compare.rows.map(([k, v]) => <li key={k} className="text-sm"><span className="font-display text-lg font-semibold text-[#e8c979]">{k}</span><span className="mx-2 text-white/30">=</span><span className="text-white/80">{v}</span></li>)}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div id="piattaforma" data-testid="offer-card-early_access" className="mt-10 grid gap-6 rounded-[28px] border border-white/10 bg-white/[0.02] p-6 md:grid-cols-12 md:items-center md:p-9">
            <div className="md:col-span-8">
              <p className="eyebrow mb-4">{o.earlyAccess.eyebrow}</p>
              <h3 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{o.earlyAccess.name}</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/75 md:text-base">{o.earlyAccess.text}</p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <button data-testid="offer-cta-early_access" onClick={() => onRequest("early_access")} className="btn-ghost group w-full justify-center md:w-auto">
                {o.earlyAccess.cta} <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
